/**
 * The single focus ring (README.md, Atoms > Button; DESIGN.md, Components):
 * a 2px solid outline, 2px out from the element, in `--color-focus-ring`.
 * Identical on every focusable atom and never restyled per component. Only
 * for `:focus-visible`, so a keyboard user always sees where they are and a
 * mouse click draws nothing.
 *
 * ── Why `outline-solid` is spelled out ─────────────────────────────────────
 *
 * Under Tailwind v4, `outline-none` is not the transparent outline v3's was.
 * It sets `outline-style: none` *and* the custom property
 * `--tw-outline-style: none`, on the element itself rather than only at rest.
 * `outline-2` then compiles to `outline-style: var(--tw-outline-style)` — so
 * on focus it read back the `none` that `outline-none` had left there, and
 * the ring was a 2px outline of style `none`: nothing. That was every
 * focusable control in the design system, silently, until the style was set
 * explicitly for the focused state. `focus-ring.test.ts` resolves the
 * compiled CSS and holds that line.
 *
 * The colour is a token rather than `primary` written in, so the ring can
 * follow its surface: a subtree marked `focus-ring-on-dark` (the indigo
 * sidebar) turns it to the accent, with nothing to change on the controls.
 *
 * The colour is set at rest too, not only on focus, and that is deliberate.
 * Most controls carry `transition-colors`, which in Tailwind v4 transitions
 * `outline-color` — so a colour applied only on focus faded in from
 * `currentColor`, and the ring appeared for its first 120ms in the label's
 * colour: near-white around a primary button. At rest the outline's style is
 * `none`, so its colour draws nothing until focus gives it a style.
 *
 * An outline rather than a box-shadow on purpose: in forced-colours mode
 * (Windows High Contrast) shadows are dropped, and an outline is kept and
 * repainted in the system's own highlight colour.
 */
export const FOCUS_RING = 'outline-none outline-focus-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:outline-offset-2';
/**
 * The same ring, drawn 2px inside the element's edge instead of outside it.
 *
 * For a control that sits flush against something that clips: the bottom
 * navigation bar against the edge of the viewport, a row inside a scrolling
 * or rounded container. An outward ring there is cut off on the sides that
 * touch the edge, and a ring missing two sides is not a ring. Prefer fixing
 * the container; this is for the cases where the edge is the point.
 */
export const FOCUS_RING_INSET = 'outline-none outline-focus-ring focus-visible:outline-solid focus-visible:outline-2 focus-visible:-outline-offset-2';
/**
 * The same ring, on an element that is not itself focused but holds the
 * control that is: the question option card, a label wrapped around a
 * visually hidden radio. The card is what the student sees, so the card
 * takes the ring. A selected card's own border stays as it is, so selected,
 * focused, and both are three different looks.
 */
export const FOCUS_RING_WITHIN = 'outline-focus-ring has-[:focus-visible]:outline-solid has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2';
/**
 * The class a dark surface carries so the rings inside it switch to the
 * accent. Defined in the theme (`@utility focus-ring-on-dark`); exported so
 * the class is written in one place.
 */
export const FOCUS_RING_ON_DARK = 'focus-ring-on-dark';
//# sourceMappingURL=focus-ring.js.map